import {
  addClientService,
  getAllClientService,
  getClientDetailService,
  getClientFilterService,
  updateClientService,
  resendClientEmailService,
} from "@/service/client.service";
import { sendOnboardEmailService, OnboardEmailPayload } from "@/service/email.service";
import {
  Client,
  ClientFilterListResponse,
  ClientList,
  ClientQuery,
} from "@/types/client.model";
import { errorToast, successToast } from "@/utils/toast";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";

interface ClientState {
  clients: ClientList[];
  filteredClients: ClientList[];
  noClients: boolean;
  searchTerm: string;
  loading: boolean;
  error: string | null;
  clientOptions: ClientFilterListResponse[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

const initialState: ClientState = {
  clients: [],
  filteredClients: [],
  noClients: false,
  searchTerm: "",
  loading: false,
  clientOptions: [],
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 20,
  },
  sortBy: undefined,
  sortOrder: undefined,
};

export const fetchClientOptions = createAsyncThunk(
  "clients/fetchClientOptions",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getClientFilterService({ showAll: true });
      return data;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch client options";
      return rejectWithValue(message);
    }
  }
);

// Get client detail thunk
export const getClientDetail = createAsyncThunk(
  "clients/getClientDetail",
  async (clientId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await getClientDetailService(clientId);
      const clientItem = data.data as typeof data.data & {
        cc?: string[];
        bcc?: string[];
      };

      return {
        id: clientItem.id,
        first_name: clientItem.user?.first_name || "",
        last_name: clientItem.user?.last_name || "",
        name: `${clientItem.user?.first_name || ""} ${clientItem.user?.last_name || ""
          }`.trim(),
        org_name: clientItem.org_name,
        email: clientItem.user?.email || "",
        cc: clientItem.cc ?? [],
        bcc: clientItem.bcc ?? [],
        contact_number: clientItem.user?.contact_number || "",
        website: clientItem.website || "",
        address: clientItem.location?.address || "",
        city: clientItem.location?.city || "",
        state: clientItem.location?.state || "",
        pincode: clientItem.location?.pincode || "",
        country: clientItem.location?.country || "",
        location: clientItem.location
          ? `${clientItem.location.city || ""} ${clientItem.location.city && clientItem.location.state ? ", " : ""
          } ${clientItem.location.state || ""}`
          : "",
        status: clientItem.user?.status,
      };
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch client details";
      dispatch(setError(message));
      errorToast(message, "");
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Thunk action to fetch clients from API
export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async (
    clientQuery: ClientQuery = { page: 1, pageSize: 20 },
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));

      // Get current filters from state if not provided in query
      const state = getState() as RootState;
      const currentQuery: ClientQuery = {
        page: clientQuery.page || state.clients.pagination.currentPage,
        pageSize: clientQuery.pageSize || state.clients.pagination.pageSize,
        search:
          clientQuery.search !== undefined
            ? clientQuery.search
            : state.clients.searchTerm || undefined,
        sortBy: state.clients.sortBy,
        sortOrder: state.clients.sortOrder,
      };

      const {
        data: { data, pagination },
      } = await getAllClientService(currentQuery);

      dispatch(setNoClients(false));

      return {
        clients: data,
        pagination: {
          currentPage: pagination?.current_page as number,
          totalPages: pagination?.total_pages as number,
          totalItems: pagination?.total_items as number,
          pageSize: pagination?.page_size as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoClients(true));
        throw error;
      }

      const message =
        error.response?.data?.message || "Failed to fetch clients";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addClient = createAsyncThunk(
  "clients/addClient",
  async (clientData: Client, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const { data } = await addClientService(clientData);

      successToast("Client added successfully", "");

      const state = getState() as RootState;
      dispatch(
        fetchClients({
          page: state.clients.pagination.currentPage,
          pageSize: state.clients.pagination.pageSize,
        })
      );

      const created = data.data; // APIResponse<ClientList>
      const createdClient: Client = {
        id: created.user?.id || created.id,
        first_name: created.user?.first_name || "",
        last_name: created.user?.last_name || "",
        org_name: created.org_name,
        email: created.user?.email || "",
        contact_number: created.user?.contact_number || "",
        website: created.website || "",
        address: created.location?.address || "",
        city: created.location?.city || "",
        state: created.location?.state || "",
        pincode: created.location?.pincode || "",
        country: created.location?.country || "",
        email_prefill: {
          subject: created?.email_prefill?.subject,
          body: created?.email_prefill?.body,
        },
        location: created.location
          ? `${created.location.city || ""} ${created.location.city && created.location.state ? ", " : ""
          } ${created.location.state || ""}`
          : "",
        status: created.user?.status as any,
      };
      return createdClient;
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to add client";
      dispatch(setError(message));

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const sendClientOnboardEmail = createAsyncThunk(
  "clients/sendClientOnboardEmail",
  async (
    {
      clientId,
      payload,
    }: { clientId: string; payload: OnboardEmailPayload },
    { dispatch }
  ) => {
    try {
      dispatch(setLoading(true));
      await sendOnboardEmailService(clientId, payload);
      successToast("Onboarding email sent", "");
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to send email";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const resendClientEmail = createAsyncThunk(
  "clients/resendClientEmail",
  async (
    {
      userId,
      payload,
    }: { userId: string; payload: OnboardEmailPayload },
    { dispatch }
  ) => {
    try {
      dispatch(setLoading(true));
      await resendClientEmailService(userId, payload);
      successToast("Email resent successfully", "");
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to resend email";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async (clientData: Client, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const { data } = await updateClientService(clientData.id!, clientData);

      successToast("Client updated successfully", "");

      const state = getState() as RootState;
      dispatch(
        fetchClients({
          page: state.clients.pagination.currentPage,
          pageSize: state.clients.pagination.pageSize,
        })
      );

      const updated = (data as any).data;
      const updatedClient: Client = {
        id: updated.user?.id || updated.id,
        first_name: updated.user?.first_name || "",
        last_name: updated.user?.last_name || "",
        org_name: updated.org_name,
        email: updated.user?.email || "",
        contact_number: updated.user?.contact_number || "",
        website: updated.website || "",
        address: updated.location?.address || "",
        city: updated.location?.city || "",
        state: updated.location?.state || "",
        pincode: updated.location?.pincode || "",
        country: updated.location?.country || "",
        cc: updated.cc || [],
        bcc: updated.bcc || [],
        email_prefill: {
          subject: updated?.email_prefill?.subject,
          body: updated?.email_prefill?.body,
        },
        location: updated.location
          ? `${updated.location.city || ""} ${
              updated.location.city && updated.location.state ? ", " : ""
            } ${updated.location.state || ""}`
          : "",
        status: updated.user?.status as any,
      };

      return updatedClient;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to update client";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Create the slice with extraReducers
const clientSliceWithReducers = createSlice({
  name: "clients",
  initialState,
  reducers: {
    setNoClients: (state, action: PayloadAction<boolean>) => {
      state.noClients = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSort(state, action: PayloadAction<{ sortBy: string; sortOrder: "asc" | "desc" }>) {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    resetFilters: (state) => {
      state.searchTerm = "";
      state.sortBy = undefined;
      state.sortOrder = undefined;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.clients;
        state.filteredClients = action.payload.clients;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch clients";
      })
      .addCase(fetchClientOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientOptions.fulfilled, (state, action) => {
        state.clientOptions = action.payload;
        state.loading = false;
      })
      .addCase(fetchClientOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch client options";
      })
      .addCase(addClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to add client";
      })
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update client";
      })
      .addCase(getClientDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClientDetail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getClientDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch client details";
      });
  },
});

// Extract actions to use in thunks
export const {
  setLoading,
  setError,
  setSearchTerm,
  resetFilters,
  resetAllState,
  setNoClients,
  setSort,
} = clientSliceWithReducers.actions;

// Selectors
export const selectClients = (state: RootState) => state.clients.clients;
export const selectFilteredClients = (state: RootState) =>
  state.clients.filteredClients;
export const selectSearchTerm = (state: RootState) => state.clients.searchTerm;
export const selectClientLoading = (state: RootState) => state.clients.loading;
export const selectClientError = (state: RootState) => state.clients.error;
export const selectPagination = (state: RootState) => state.clients.pagination;
export const selectClientOptions = (state: RootState) =>
  state.clients.clientOptions;
export const selectNoClients = (state: RootState) => state.clients.noClients;
export const selectSortBy = (state: RootState) => state.clients.sortBy;
export const selectSortOrder = (state: RootState) => state.clients.sortOrder;

export default clientSliceWithReducers.reducer;
